import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  useQariDocuments,
  useQariMutations,
} from '@/hooks/useQariProfile';
import { cn } from '@/lib/utils/cn';
import type {
  QariDocument,
  QariDocumentKind,
} from '@/types/qari';
import {
  WizardShell,
  renderDraftGuard,
  useQariDraftProfile,
} from '@/components/qari/wizard';
import {
  IconClose,
  IconKey,
  IconLock,
  IconShield,
  IconUpload,
} from '@/components/home/icons';

/**
 * /qari/onboarding/verification — Step 3: Final Verification.
 *
 * Two upload zones (Identity Document + Teaching Credentials) backed by
 * the existing `qari_documents` storage + table via `uploadQariDocument`,
 * plus a background-check authorization checkbox.
 *
 * Submit Application calls `submitForReview` (status → pending_review,
 * persisted by the DB trigger) and redirects to /qari/onboarding/submitted.
 *
 * The consent checkbox is local UI state — there's no schema field for
 * it yet, so we gate the submit on it client-side but don't persist it.
 */
export function QariOnboarding4() {
  useDocumentTitle('Qari Application · Verification');

  const draft = useQariDraftProfile();
  const guard = renderDraftGuard(draft);
  if (guard) return guard;
  return <Step />;
}

function Step() {
  const navigate = useNavigate();
  const { submitForReview } = useQariMutations();
  const docs = useQariDocuments();

  const [consent, setConsent] = useState(false);

  const identityDocs = docs.data?.filter((d) => d.kind === 'identity') ?? [];
  const credentialDocs = docs.data?.filter(
    (d) => d.kind === 'ijazah' || d.kind === 'certificate',
  ) ?? [];

  const canSubmit =
    identityDocs.length > 0 &&
    credentialDocs.length > 0 &&
    consent &&
    !submitForReview.isPending;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    try {
      await submitForReview.mutateAsync();
      navigate('/qari/onboarding/submitted', { replace: true });
    } catch {
      /* surfaced via submitForReview.isError below */
    }
  }, [canSubmit, submitForReview, navigate]);

  return (
    <WizardShell
      currentStep={3}
      title="Step 3: Final Verification · 100% Complete"
      heading="Final Verification"
      subtitle="To maintain the highest standards of Quranic education, we require secure document verification. Your data is encrypted and handled with absolute confidentiality."
      prev={{
        kind: 'link',
        label: 'Back',
        to: '/qari/onboarding/expertise',
      }}
      next={{
        kind: 'action',
        label: submitForReview.isPending
          ? 'Submitting…'
          : 'Submit Application',
        onClick: handleSubmit,
        pending: submitForReview.isPending,
        disabled: !canSubmit,
      }}
      hint={
        canSubmit
          ? 'Submitting moves your application into review. You will be notified by email.'
          : 'Upload an identity document, your credentials, and grant background-check authorization to enable submit.'
      }
      extras={
        submitForReview.isError ? (
          <div
            role="alert"
            className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200"
          >
            Could not submit your application:{' '}
            {submitForReview.error instanceof Error
              ? submitForReview.error.message
              : 'Unknown error.'}
          </div>
        ) : null
      }
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <UploadZone
          kind="identity"
          icon={<IconKey className="h-6 w-6" />}
          title="Identity Document"
          description="Upload a clear photo or scan of your Passport, National ID, or Driver's License."
          accept="image/jpeg,image/png,image/webp,application/pdf"
          docs={identityDocs}
        />
        <UploadZone
          kind="ijazah"
          icon={<IconShield className="h-6 w-6" />}
          title="Teaching Credentials"
          description="Upload your Ijazah, Islamic Studies degree, or relevant teaching certifications."
          accept="image/jpeg,image/png,image/webp,application/pdf"
          docs={credentialDocs}
        />

        {/* Background check authorization spans full width */}
        <div className="rounded-2xl border border-night-500/30 bg-night-900/40 p-6 backdrop-blur-md md:col-span-2 md:p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center">
            <div className="flex-1">
              <div className="mb-3 flex items-center gap-3 text-beam">
                <IconLock className="h-6 w-6" />
                <h3 className="font-display text-lg font-bold text-night-50">
                  Background Check Authorization
                </h3>
              </div>
              <p className="mb-5 text-sm leading-relaxed text-night-200">
                As part of our commitment to student safety, we conduct global
                background checks for all verified instructors. By checking
                the box below, you authorize Alhudah to perform this
                verification.
              </p>
              <label className="flex cursor-pointer select-none items-center gap-3">
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                    consent
                      ? 'border-beam bg-beam text-beam-ink'
                      : 'border-night-500/60 bg-night-900/60',
                  )}
                  aria-hidden="true"
                >
                  {consent && (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3.5 w-3.5"
                    >
                      <path d="m5 12 5 5L20 7" />
                    </svg>
                  )}
                </span>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="sr-only"
                />
                <span
                  className={cn(
                    'text-xs font-semibold uppercase tracking-wide transition-colors',
                    consent ? 'text-beam' : 'text-night-100',
                  )}
                >
                  I agree to the background-check protocols
                </span>
              </label>
            </div>
            <div className="flex aspect-video w-full items-end overflow-hidden rounded-xl border border-night-500/30 bg-gradient-to-br from-night-700 to-night-900 md:w-1/3">
              <div className="flex items-center gap-2 p-4">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-beam" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-beam">
                  Secure Encryption Active
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WizardShell>
  );
}

/* ================================================================== *
 * Upload zone
 * ================================================================== */

function UploadZone({
  kind,
  icon,
  title,
  description,
  accept,
  docs,
}: {
  kind: QariDocumentKind;
  icon: ReactNode;
  title: string;
  description: string;
  accept: string;
  docs: QariDocument[];
}) {
  const { uploadDocument, deleteDocument } = useQariMutations();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setUploading(true);
      setError(null);
      try {
        await uploadDocument.mutateAsync({ kind, file });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed.');
      } finally {
        setUploading(false);
      }
    },
    [kind, uploadDocument],
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void handleFile(file);
  };

  const onDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) void handleFile(file);
  };

  const onDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const onDragLeave = () => setIsDragOver(false);

  const onRemove = async (doc: QariDocument) => {
    setDeletingId(doc.id);
    try {
      await deleteDocument.mutateAsync(doc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove file.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-night-500/30 bg-night-900/40 p-6 backdrop-blur-md md:p-8">
      <div className="flex items-center gap-3 text-beam">
        <span>{icon}</span>
        <h3 className="font-display text-lg font-bold text-night-50">
          {title}
        </h3>
      </div>
      <p className="text-sm leading-relaxed text-night-200">{description}</p>

      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        disabled={uploading}
        className={cn(
          'mt-1 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-10 transition-colors',
          isDragOver
            ? 'border-beam bg-beam/10'
            : 'border-night-500/60 bg-night-900/40 hover:border-beam/60 hover:bg-night-800/40',
          uploading && 'opacity-60',
        )}
      >
        <IconUpload
          className={cn(
            'h-8 w-8',
            isDragOver ? 'text-beam' : 'text-night-300',
          )}
        />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-night-200">
          {uploading
            ? 'Uploading…'
            : isDragOver
              ? 'Drop to upload'
              : 'Drop files or click to browse'}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-night-300">
          JPG, PNG, WebP, PDF · Max 10 MB
        </span>
      </button>

      <input
        ref={fileInput}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={onChange}
      />

      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-200"
        >
          {error}
        </p>
      )}

      {docs.length > 0 && (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-night-500/40 bg-night-800/60 px-3 py-2"
            >
              <span className="min-w-0 flex-1 truncate text-sm text-night-100">
                {d.original_filename}
              </span>
              <span className="shrink-0 text-[10px] uppercase tracking-wide text-night-300">
                {formatSize(d.size_bytes)}
              </span>
              <button
                type="button"
                onClick={() => onRemove(d)}
                disabled={deletingId === d.id}
                aria-label={`Remove ${d.original_filename}`}
                className="text-night-300 transition-colors hover:text-red-300 disabled:opacity-50"
              >
                <IconClose className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---- Helpers ---- */

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
